/**
 * Cookie Consent Utilities — LabComplyx Cookie Tracking Protocol
 * Based on GDPR/ePrivacy, UK PECR, and California CCPA/CPRA requirements.
 */

export const CONSENT_COOKIE_NAME = 'labcomplyx_consent';
export const VISITOR_ID_COOKIE = 'labcomplyx_vid';
export const CONSENT_VERSION = '2026-05';
export const CONSENT_LIFETIME_DAYS = 180; // ~6 months

export const COOKIE_CATEGORIES = {
  necessary: {
    label: 'Necessary',
    description: 'Required for security, login, fraud prevention, and storing your privacy choices. Always active.',
    alwaysOn: true
  },
  functional: {
    label: 'Functional',
    description: 'Helps remember preferences such as language or interface settings.',
    alwaysOn: false
  },
  analytics: {
    label: 'Analytics',
    description: 'Measures traffic and product usage to improve the website and application.',
    alwaysOn: false
  },
  advertising: {
    label: 'Advertising / Sharing',
    description: 'Enables campaign measurement, remarketing, and cross-context advertising where permitted.',
    alwaysOn: false
  }
};

export function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name, value, days = CONSENT_LIFETIME_DAYS) {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function deleteCookie(name) {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

export function readConsent() {
  const raw = getCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Invalidate stale consent if policy version changed
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(categories) {
  const state = {
    ...categories,
    version: CONSENT_VERSION,
    ts: new Date().toISOString()
  };
  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(state));
  return state;
}

export function canLoad(category) {
  const consent = readConsent();
  if (category === 'necessary') return true;
  // Functional/analytics/advertising default ON until a visitor explicitly opts out
  if (!consent) return true;
  return Boolean(consent[category]);
}

export function getVisitorId() {
  let vid = getCookie(VISITOR_ID_COOKIE);
  if (!vid) {
    vid = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    setCookie(VISITOR_ID_COOKIE, vid, 365);
  }
  return vid;
}

/**
 * Detect if the visitor may be from EU/UK based on browser locale.
 * NOTE: This is an approximation only. For strict legal compliance in production,
 * use a server-side geo-IP lookup.
 */
export function detectRegion() {
  // Check for Global Privacy Control signal first (California/US)
  if (navigator.globalPrivacyControl === true) return 'CA';

  const lang = navigator.language || navigator.userLanguage || '';
  const euLocales = ['de', 'fr', 'es', 'it', 'nl', 'pl', 'pt', 'sv', 'da', 'fi', 'nb', 'cs', 'hu', 'ro', 'sk', 'el', 'bg', 'hr', 'lv', 'lt', 'et', 'sl', 'mt', 'ga'];
  const prefix = lang.split('-')[0].toLowerCase();
  const country = (lang.split('-')[1] || '').toUpperCase();

  if (country === 'GB' || prefix === 'en-gb') return 'UK';
  if (country === 'US' || lang.toLowerCase().startsWith('en-us')) return 'US';
  if (euLocales.includes(prefix)) return 'EU';

  return 'OTHER';
}

export function hasGPCSignal() {
  return navigator.globalPrivacyControl === true || window.globalPrivacyControl === true;
}

/**
 * Returns true if we need to show the consent banner for this visitor.
 * EU/UK always need it. CA/US/OTHER show it as a best practice.
 */
export function requiresConsentBanner() {
  const existing = readConsent();
  return !existing; // show if no valid consent recorded
}